import React, { useState, useEffect, useRef, useContext } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Button } from 'primereact/button'
import { Toast } from 'primereact/toast'
import { Dialog } from 'primereact/dialog'
import { Calendar } from 'primereact/calendar'
import { InputText } from 'primereact/inputtext'
import MainContentLayout from '../../Layout/MainContentLayout'
import { formatCurrency, formatDate, ToastPopUp } from '../../shared/utils'
import server from '../../../server'
import originsDirect from '../../../assets/images/ORIGINS-DIRECT.png'
import originsManganegment from '../../../assets/images/ORIGINS-MANAGEMENT.png'
import originsDirectInstant from '../../../assets/images/ORIGINS-DIRECT-INSTANT.png'
import { AuthenticationContext } from '../../../Auth/authentication.context'
import useTableRowsPerPage from '../../../hooks/useTableRowsPerPage'
import { getInitialPaginationState } from '../../../shared/tablePagination'

function systemBodyTemplate(payable) {
  if (!payable.system || payable.system === 'Origins Direct') {
    return (
      <div className='d-flex justify-content-center'>
        <img src={originsDirect} alt='Origins Direct' style={{ height: '20px' }} />
      </div>
    )
  }
  if (payable.system === 'Origins Direct Instant') {
    return (
      <div className='d-flex justify-content-center'>
        <img src={originsDirectInstant} alt='Origins Direct Instant' style={{ height: '20px' }} />
      </div>
    )
  }
  if (payable.system === 'Origins Management') {
    return (
      <div className='d-flex justify-content-center'>
        <img src={originsManganegment} alt='Origins Management' style={{ height: '20px' }} />
      </div>
    )
  }
  return <span>{payable.system}</span>
}

const initialPaymentForm = () => ({
  paymentDate: new Date(),
  transactionId: '',
})

export default function PaPayablesPharmacyDetail() {
  const { pharmacyId } = useParams()
  const navigate = useNavigate()
  const toast = useRef(null)
  const { user } = useContext(AuthenticationContext)
  const { defaultRows, rowsPerPageOptions, applyPageEvent } = useTableRowsPerPage()
  const [pagination, setPagination] = useState(() =>
    getInitialPaginationState(user)
  )

  useEffect(() => {
    setPagination((prev) => ({ ...prev, rows: defaultRows }))
  }, [defaultRows])
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [paying, setPaying] = useState(false)
  const [paymentModalVisible, setPaymentModalVisible] = useState(false)
  const [paymentForm, setPaymentForm] = useState(initialPaymentForm())

  const openPaymentModal = () => {
    setPaymentForm(initialPaymentForm())
    setPaymentModalVisible(true)
  }

  const fetchOrders = () => {
    setLoading(true)
    server
      .get(`/admins/payables/pharmacies/${pharmacyId}/orders`)
      .then((response) => {
        setData(response.data.data)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    fetchOrders()
  }, [pharmacyId])

  const handleConfirmPayment = () => {
    setPaying(true)
    const body = {
      paPaymentDate: paymentForm.paymentDate
        ? paymentForm.paymentDate.toISOString()
        : new Date().toISOString(),
      transactionId: paymentForm.transactionId?.trim() || undefined,
    }
    server
      .post(
        `/admins/payables/pharmacies/${pharmacyId}/pay`,
        body,
        {
        }
      )
      .then(() => {
        ToastPopUp(toast, 'success', 'Success', 'PA payment completed successfully.')
        setPaymentModalVisible(false)
        fetchOrders()
        setPaying(false)
      })
      .catch((err) => {
        ToastPopUp(
          toast,
          'error',
          'Error',
          err.response?.data?.message || 'Failed to complete payment'
        )
        setPaying(false)
      })
  }

  const paymentModalFooter = (
    <>
      <Button
        label='Cancel'
        icon='pi pi-times'
        className='p-button-text'
        onClick={() => setPaymentModalVisible(false)}
        disabled={paying}
      />
      <Button
        label='Confirm payment'
        icon='pi pi-check'
        loading={paying}
        disabled={paying}
        onClick={handleConfirmPayment}
      />
    </>
  )

  if (!data) {
    return (
      <MainContentLayout title='PA Payables'>
        {loading ? <p>Loading...</p> : <p>Pharmacy not found or not eligible.</p>}
      </MainContentLayout>
    )
  }

  const { pharmacy, payables, totalConfirmedAmount, totalPaAmountDue } = data
  const hasPending = payables && payables.length > 0

  return (
    <MainContentLayout title={`PA Payables – ${pharmacy.name}`}>
      <Toast ref={toast} />
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <Button
          label='Back to PA Payables'
          icon='pi pi-arrow-left'
          className='p-button-text'
          onClick={() => navigate('/admin/reports/paPayables')}
        />
        {hasPending && (
          <Button
            label='Make payment'
            icon='pi pi-credit-card'
            onClick={openPaymentModal}
          />
        )}
      </div>
      {pharmacy.groupName && (
        <p className='text-muted mb-2'>Group: {pharmacy.groupName}</p>
      )}
      <div className='d-flex gap-3 mb-3 flex-wrap'>
        <span className='p-2 rounded' style={{ backgroundColor: '#f0f0f0' }}>
          <strong>Total confirmed amount (no PA paid):</strong>{' '}
          {formatCurrency(totalConfirmedAmount)}
        </span>
        <span className='p-2 rounded' style={{ backgroundColor: '#e8f5e9' }}>
          <strong>Total PA amount due:</strong> {formatCurrency(totalPaAmountDue)}
        </span>
        <span className='p-2 rounded' style={{ backgroundColor: '#e3f2fd' }}>
          <strong>Orders:</strong> {payables.length}
        </span>
      </div>
      <Dialog
        header='Make PA payment'
        visible={paymentModalVisible}
        onHide={() => !paying && setPaymentModalVisible(false)}
        footer={paymentModalFooter}
        style={{ width: '24rem' }}
        modal
        closable={!paying}
      >
        <div className='flex flex-column gap-3'>
          <div>
            <label htmlFor='payment-date' className='block mb-1 font-medium'>
              Payment date
            </label>
            <Calendar
              id='payment-date'
              value={paymentForm.paymentDate}
              onChange={(e) =>
                setPaymentForm((prev) => ({ ...prev, paymentDate: e.value || new Date() }))
              }
              dateFormat='yy-mm-dd'
              showIcon
            />
          </div>
          <div>
            <label htmlFor='transaction-id' className='block mb-1 font-medium'>
              Transaction / Cheque number
            </label>
            <InputText
              id='transaction-id'
              value={paymentForm.transactionId}
              onChange={(e) =>
                setPaymentForm((prev) => ({ ...prev, transactionId: e.target.value }))
              }
              placeholder='e.g. CHQ-12345 or TXN-...'
              className='w-100'
            />
          </div>
        </div>
      </Dialog>

      <DataTable
        value={payables || []}
        loading={loading}
        dataKey='id'
        showGridlines
        emptyMessage='No pending PA orders for this pharmacy.'
        paginator
        rows={pagination.rows}
        first={pagination.first}
        onPage={(e) => setPagination((prev) => applyPageEvent(prev, e))}
        rowsPerPageOptions={rowsPerPageOptions}
      >
        <Column field='supplierInvoiceNo' header='Order No.' />
        <Column field='system' header='Systems' body={systemBodyTemplate} />
        <Column
          field='createdAt'
          header='Date of Confirmed Order'
          body={(row) => (row.createdAt ? formatDate(row.createdAt) : '-')}
        />
        <Column
          field='originalValue'
          header='Original Value'
          body={(row) => formatCurrency(row.originalValue)}
        />
        <Column
          field='estimatedPA'
          header='Estimated PA Amount'
          body={(row) => formatCurrency(row.estimatedPA)}
        />
        <Column
          field='confirmedExtendTotal'
          header='Confirmed Value'
          body={(row) => formatCurrency(row.confirmedExtendTotal)}
        />
        <Column
          field='confirmedPaValue'
          header='PA Amount'
          body={(row) => formatCurrency(row.confirmedPaValue)}
        />
      </DataTable>
    </MainContentLayout>
  )
}
